import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Layout from "../../Component/Layout";
import Select, { components as SelectComponents } from "react-select";
import { FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { ORDER_API, MASTER_API } from "../../apiConfig"; // adjust relative path if this file lives elsewhere

// checklist.routes.js is mounted at /checklist inside order-service
const API_BASE = `${ORDER_API}/checklist`;

const toOpts = (arr) => (arr || []).map((v) => ({ value: String(v), label: String(v) }));

// Shows the full option text as a native browser tooltip on hover, for the
// compact table-row Selects (UOM / Type of Analysis) where long values get
// truncated to fit the column width.
const SingleValueWithTooltip = (props) => (
  <SelectComponents.SingleValue {...props}>
    <span title={props.data?.label || ""}>{props.children}</span>
  </SelectComponents.SingleValue>
);

const smallLabel = { fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "6px", display: "block" };
// Matches Product.js `styles.erpTh` / `styles.erpTd`
const thStyle = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", textAlign: "left", whiteSpace: "nowrap" };
const tdStyle = { fontSize: 13, padding: "10px", borderBottom: "1px solid #f1f5f9", color: "#334155" };
// Matches Product.js `styles.compactInput`
const compactInput = { fontSize: "13px", height: "32px", padding: "4px 8px", borderRadius: "4px", border: "1px solid #ced4da", width: "100%", outline: "none" };
// Matches Product.js `customSelectStyles`
const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: "13px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px", height: "32px", display: "flex", alignItems: "center" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px",
  }),
};

// Same cascading multi-select styling used by InspectionCharacteristics.js's filter bar
const filterSelectStyles = {
  control: (base) => ({ ...base, minHeight: 30, height: "auto", fontSize: "12px", borderColor: "#dee2e6", boxShadow: "none", "&:hover": { borderColor: "#adb5bd" } }),
  valueContainer: (base) => ({ ...base, padding: "2px 6px", flexWrap: "wrap" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "12px" }),
  indicatorsContainer: (base) => ({ ...base, alignSelf: "flex-start", paddingTop: 2 }),
  dropdownIndicator: (base) => ({ ...base, padding: 2 }),
  clearIndicator: (base) => ({ ...base, padding: 2 }),
  placeholder: (base) => ({ ...base, fontSize: "12px", color: "#94a3b8", fontWeight: 400 }),
  option: (base, state) => ({ ...base, fontSize: "12px", fontWeight: 400, color: state.isSelected ? "#fff" : "#334155", backgroundColor: state.isSelected ? "#475569" : state.isFocused ? "#f1f5f9" : "#fff", padding: "6px 10px" }),
  multiValue: (base) => ({ ...base, fontSize: "11px", backgroundColor: "#e2e8f0", borderRadius: 4 }),
  multiValueLabel: (base) => ({ ...base, fontSize: "11px", color: "#334155", padding: "1px 4px" }),
  multiValueRemove: (base) => ({ ...base, color: "#64748b", ":hover": { backgroundColor: "#cbd5e1", color: "#1e293b" } }),
  menu: (base) => ({ ...base, fontSize: "12px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};

// Subset of InspectionCharacteristics.js's `styles` object, copied 1:1 so the
// Assignment tab looks and behaves exactly the same.
const styles = {
  filterCard:     { background: "#ffffff", borderRadius: "12px", padding: "18px", boxShadow: "0 2px 8px rgba(15,23,42,0.06)", border: "1px solid #e2e8f0", marginBottom: "18px" },
  compactLabel:   { fontSize: "12px", fontWeight: 600, marginBottom: "4px", color: "#334155", display: "block" },
  filterBar:      { display: "flex", alignItems: "flex-end", gap: 8, flexWrap: "nowrap", overflowX: "auto", padding: "4px 0" },
  filterLabel:    { fontSize: 11, fontWeight: 600, color: "#64748b", whiteSpace: "nowrap", letterSpacing: "0.02em", paddingLeft: 2 },
  clearBtn:       { border: "1px solid #cbd5e1", borderRadius: 4, background: "#fff", fontSize: 11, color: "#64748b", padding: "0 10px", height: 30, cursor: "pointer", whiteSpace: "nowrap" },
  matchHint:      { marginTop: 8, fontSize: 12, color: "#475569" },
  toggleBar:      { display: "flex", gap: "0px", marginBottom: "12px", borderBottom: "1px solid #e2e8f0", paddingBottom: "10px" },
  toggleBtn:      { border: "none", borderRadius: "6px", padding: "6px 20px", fontSize: "13px", fontWeight: 600, cursor: "pointer", transition: "all 0.15s" },
  toggleBtnActive:   { background: "#021843", color: "#ffffff" },
  toggleBtnInactive: { background: "#f1f5f9", color: "#64748b" },
  erpTable:       { width: "100%", borderCollapse: "collapse" },
  erpTh:          { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "10px", borderBottom: "1px solid #e5e7eb", color: "#1e293b" },
  erpTd:          { fontSize: 13, padding: "10px", borderBottom: "1px solid #f1f5f9", color: "#334155" },
  modalBackdrop:  { position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999, padding: "20px" },
  modal:          { width: "95%", maxWidth: "1280px", height: "92vh", background: "#ffffff", borderRadius: "16px", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 15px 40px rgba(0,0,0,0.18)" },
  modalHeader:    { padding: "18px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#ffffff" },
  closeBtn:       { border: "none", background: "transparent", fontSize: "24px", color: "#64748b", cursor: "pointer" },
  modalBody:      { flex: 1, overflowY: "auto", padding: "22px 24px", background: "#f8fafc" },
  modalFooter:    { padding: "16px 24px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", background: "#ffffff" },
  saveBtn:        { background: "#021843", color: "#fff", border: "none", borderRadius: "8px", padding: "10px 26px", fontSize: "14px", fontWeight: 600, cursor: "pointer" },
};

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24"
    fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" /><path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

// Every filter criterion a checklist assignment can carry, in display order.
// Shared by the Assignment tab table (inline chips) and the View Assignment modal.
const ASSIGNMENT_FILTER_FIELDS = [
  ["Category",      "product_category"],
  ["Sub Category",  "sub_category"],
  ["Quality Cat.",  "quality_category"],
  ["Cable Type",    "cable_type"],
  ["Code",          "code"],
  ["Color",         "colour"],
  ["Cores",         "no_of_cores"],
  ["Cross Section", "nominal_cross_section"],
  ["Std. Ref",      "reference_of_standard"],
  ["Voltage",       "voltage"],
  ["Construction",  "construction"],
  ["Product Code",  "product_code_filter"],
];

const AssignmentFilterChips = ({ row }) => {
  const chips = ASSIGNMENT_FILTER_FIELDS.filter(([, key]) => row[key]);
  if (chips.length === 0) {
    return <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
      {chips.map(([label, key]) => (
        <span key={key} style={{ background: "#e2e8f0", borderRadius: 4, padding: "2px 7px", fontSize: 11, whiteSpace: "nowrap" }}>
          <span style={{ color: "#64748b", fontWeight: 600 }}>{label}: </span>
          <span style={{ color: "#1e293b" }}>{row[key]}</span>
        </span>
      ))}
    </div>
  );
};

const emptyProdFilters = {
  productCategory: [], subCategory: [], qualityCategory: [],
  cableType: [], code: [], colour: [], noOfCores: [],
  nominalCrossSection: [], referenceOfStandard: [], voltage: [], construction: [],
};

function Checklist() {
  /* ── Checklist tab (unchanged) ── */
  const [checklistList, setChecklistList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [parameters, setParameters] = useState([{ name: "", uom: "", analysisType: "" }]);
  const [checklistLogs, setChecklistLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("parameters");
  const [errorMsg, setErrorMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [checklistNumberInput, setChecklistNumberInput] = useState("");

  const [uomOptions, setUomOptions] = useState([]);
  const [analysisOptions, setAnalysisOptions] = useState([]);

  /* ── Page-level tab: CHECKLIST vs ASSIGNMENT ── */
  const [tableView, setTableView] = useState("checklist");

  /* ── Assignment tab: dropdowns (cascading, same shape as inspection) ── */
  const [ddl, setDdl] = useState({
    productTypes: [], productCategories: [], subCategories: [], qualityCategories: [],
    cableTypes: [], constructions: [], codes: [], colours: [], cores: [],
    nominalCrossSections: [], standards: [], voltages: [], productCodes: [],
    categoryToCableTypes: {}, cableTypeToCode: {},
    categoryToSubCategory: {}, subCategoryToQualityCategory: {},
    allProducts: [],
  });
  const [allProducts, setAllProducts] = useState([]);

  /* ── Assignment tab: filter bar state (persistent, interlinked) ── */
  const [assignProductType, setAssignProductType] = useState("");
  const [prodFilterSelections, setProdFilterSelections] = useState(emptyProdFilters);
  const [assignProductCode, setAssignProductCode] = useState("");
  const [assignProductDesc, setAssignProductDesc] = useState("");
  const [prodMatchCount, setProdMatchCount] = useState(null);

  /* ── Assignment tab: assignment list + modals ── */
  const [assignmentList, setAssignmentList] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignChecklistSelected, setAssignChecklistSelected] = useState(null);
  const [assignMatchCount, setAssignMatchCount] = useState(null);
  const [assignSaving, setAssignSaving] = useState(false);

  const [showViewAssignModal, setShowViewAssignModal] = useState(false);
  const [viewAssignRow, setViewAssignRow] = useState(null);

  /* ── Product tab: flat list of every assigned product, with its real attributes.
     Filter-state shape/keys mirror GTPConfiguration.js's Product Format tab
     (pfFilterSelections) — isMulti react-select per field. ── */
  const [assignedProductsList, setAssignedProductsList] = useState([]);
  const emptyProductTabFilters = {
    category: [], cableType: [], code: [], colour: [],
    noOfCores: [], nominalCrossSection: [], referenceOfStandard: [],
    voltage: [], construction: [],
  };
  const [productTabFilters, setProductTabFilters] = useState(emptyProductTabFilters);
  const [productCodeSearch, setProductCodeSearch] = useState("");
  const [productDescSearch, setProductDescSearch] = useState("");
  const [productTabPage, setProductTabPage] = useState(1);
  const PRODUCT_TAB_PAGE_SIZE = 10;

  useEffect(() => {
    setProductTabPage(1);
  }, [productTabFilters, productCodeSearch, productDescSearch, assignedProductsList]);

  useEffect(() => {
    loadChecklistList();
    loadUomList();
    loadAnalysisTypeList();
    loadDropdowns();
    loadAssignments();
    loadAssignedProducts();
  }, []);

  /* live match count while filters change */
  useEffect(() => {
    const params = buildAssignParams(prodFilterSelections, assignProductType, assignProductCode);
    const hasAny = Object.values(params).some(Boolean);
    if (!hasAny) { setProdMatchCount(null); return; }
    axios.get(`${API_BASE}/count-matching`, { params })
      .then((r) => setProdMatchCount(r.data?.count ?? null))
      .catch(() => setProdMatchCount(null));
  }, [prodFilterSelections, assignProductType, assignProductCode]);

  // UOM comes from Configuration > UOM master
  const loadUomList = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/configuration/uom`);
      const options = (res.data.data || res.data || []).map((uom) => ({
        value: uom.name,
        label: uom.name,
      }));
      setUomOptions(options);
    } catch (err) {
      console.error("Failed to load UOM list", err);
    }
  };

  // Type of Analysis comes from Configuration > Analysis Type master
  const loadAnalysisTypeList = async () => {
    try {
      const res = await axios.get(`${MASTER_API}/configuration/analysis-type`);
      const options = (res.data.data || res.data || []).map((a) => ({
        value: a.name,
        label: a.name,
      }));
      setAnalysisOptions(options);
    } catch (err) {
      console.error("Failed to load Analysis Type list", err);
    }
  };

  const loadChecklistList = async () => {
    try {
      const res = await axios.get(API_BASE);
      setChecklistList(res.data.data || res.data || []);
    } catch (err) {
      console.error("Failed to load checklists", err);
      setChecklistList([]);
    }
  };

  const loadDropdowns = async () => {
    try {
      const res = await axios.get(`${API_BASE}/dropdowns`);
      const base = res.data || {};
      setAllProducts(base.allProducts || []);
      setDdl({
        ...base,
        codes:                base.codes || [],
        colours:              base.colours || [],
        cores:                base.cores || [],
        nominalCrossSections: base.nominalCrossSections || [],
        standards:            base.standards || [],
        voltages:             base.voltages || [],
        productCodes:         base.productCodes || [],
        categoryToCableTypes: base.categoryToCableTypes || {},
        cableTypeToCode:      base.cableTypeToCode || {},
        categoryToSubCategory: base.categoryToSubCategory || {},
        subCategoryToQualityCategory: base.subCategoryToQualityCategory || {},
      });
    } catch (err) {
      console.error("Failed to load product dropdowns", err);
    }
  };

  const loadAssignments = async () => {
    try {
      const r = await axios.get(`${API_BASE}/assignments`);
      setAssignmentList(r.data || []);
    } catch (err) {
      console.error("Failed to load assignments", err);
      setAssignmentList([]);
    }
  };

  const loadAssignedProducts = async () => {
    try {
      const r = await axios.get(`${API_BASE}/assigned-products`);
      setAssignedProductsList(r.data || []);
    } catch (err) {
      console.error("Failed to load assigned products", err);
      setAssignedProductsList([]);
    }
  };

  const buildAssignParams = (sel, productType, productCode) => ({
    productType:          productType || undefined,
    productCategory:      sel.productCategory?.map((o) => o.value).join(",")     || undefined,
    subCategory:          sel.subCategory?.map((o) => o.value).join(",")         || undefined,
    qualityCategory:      sel.qualityCategory?.map((o) => o.value).join(",")     || undefined,
    cableType:            sel.cableType?.map((o) => o.value).join(",")           || undefined,
    code:                 sel.code?.map((o) => o.value).join(",")                || undefined,
    colour:               sel.colour?.map((o) => o.value).join(",")             || undefined,
    noOfCores:            sel.noOfCores?.map((o) => o.value).join(",")           || undefined,
    nominalCrossSection:  sel.nominalCrossSection?.map((o) => o.value).join(",") || undefined,
    referenceOfStandard:  sel.referenceOfStandard?.map((o) => o.value).join(",") || undefined,
    voltage:              sel.voltage?.map((o) => o.value).join(",")             || undefined,
    construction:         sel.construction?.map((o) => o.value).join(",")        || undefined,
    productCode:          productCode || undefined,
  });

  const openCreate = () => {
    setEditingId(null);
    setParameters([{ name: "", uom: "", analysisType: "" }]);
    setChecklistLogs([]);
    setActiveTab("parameters");
    setErrorMsg("");
    setChecklistNumberInput("");
    setShowModal(true);
  };

  const viewDetails = async (item) => {
    setEditingId(item.id);
    setParameters(item.parameters?.length ? item.parameters : [{ name: "", uom: "", analysisType: "" }]);
    setActiveTab("parameters");
    setErrorMsg("");
    setChecklistNumberInput(item.checklist_number != null ? String(item.checklist_number) : "");

    try {
      const logRes = await axios.get(`${API_BASE}/change-logs/${item.id}`);
      setChecklistLogs(logRes.data || []);
    } catch (err) {
      console.error("Failed to fetch logs", err);
      setChecklistLogs([]);
    }
    setShowModal(true);
  };

  const addParameter = () => {
    if (parameters.length < 30) {
      setParameters([...parameters, { name: "", uom: "", analysisType: "" }]);
    } else {
      toast.error("Maximum of 30 parameters allowed.");
    }
  };

  const removeParameter = (index) => {
    const updated = parameters.filter((_, i) => i !== index);
    setParameters(updated.length > 0 ? updated : [{ name: "", uom: "", analysisType: "" }]);
  };

  const updateParameter = (index, field, value) => {
    const updated = [...parameters];
    updated[index][field] = value;
    setParameters(updated);
  };

  const handleSave = async () => {
    const validParameters = parameters.filter((p) => p.name.trim() !== "");

    if (validParameters.length === 0) {
      setErrorMsg("Add at least one parameter with a name.");
      return;
    }
    for (const p of validParameters) {
      if (!p.analysisType) {
        setErrorMsg("Select a Type of Analysis for every row.");
        return;
      }
    }

    const payload = { parameters: validParameters };

    // Checklist number can only be set at creation time — once a checklist
    // exists its number stays fixed, so we only send it on create.
    if (!editingId) {
      const trimmedNumber = checklistNumberInput.trim();
      if (trimmedNumber !== "") {
        if (!/^\d+$/.test(trimmedNumber)) {
          setErrorMsg("Checklist Number must be a positive whole number.");
          return;
        }
        payload.checklistNumber = Number(trimmedNumber);
      }
    }

    setSaving(true);
    setErrorMsg("");
    try {
      if (editingId) {
        await axios.put(`${API_BASE}/${editingId}`, payload);
        toast.success("Checklist updated successfully");
      } else {
        await axios.post(API_BASE, payload);
        toast.success("Checklist created successfully");
      }
      setShowModal(false);
      loadChecklistList();
    } catch (err) {
      console.error(err);
      setErrorMsg(err.response?.data?.error || "Failed to save checklist.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const confirmed = window.confirm(`Delete checklist "${item.checklist_number}"?`);
    if (!confirmed) return;
    try {
      await axios.delete(`${API_BASE}/${item.id}`);
      toast.success("Checklist deleted successfully");
      loadChecklistList();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete checklist.");
    }
  };

  /* ══════════════════════════════════════════════
     ASSIGNMENT — computed
  ══════════════════════════════════════════════ */
  const checklistOptions = checklistList.map((c) => ({
    value: c.checklist_number,
    label: `Checklist ${c.checklist_number}`,
  }));

  const prodActiveFilterCount = Object.values(prodFilterSelections).reduce((s, a) => s + (a?.length || 0), 0)
    + (assignProductType ? 1 : 0) + (assignProductCode ? 1 : 0);

  /* Filtered product lists for assign filter bar */
  const filteredProducts = useMemo(() => {
    return allProducts.filter((row) => {
      if (assignProductType && row.productType !== assignProductType) return false;
      if (prodFilterSelections.productCategory.length && !prodFilterSelections.productCategory.some((x) => x.value === row.productCategory)) return false;
      if (prodFilterSelections.cableType.length && !prodFilterSelections.cableType.some((x) => x.value === row.cableType)) return false;
      if (prodFilterSelections.code.length && !prodFilterSelections.code.some((x) => x.value === row.code)) return false;
      if (prodFilterSelections.colour.length && !prodFilterSelections.colour.some((x) => x.value === row.color)) return false;
      if (prodFilterSelections.subCategory.length && !prodFilterSelections.subCategory.some((x) => x.value === row.subCategory)) return false;
      if (prodFilterSelections.qualityCategory.length && !prodFilterSelections.qualityCategory.some((x) => x.value === row.qualityCategory)) return false;
      return true;
    });
  }, [allProducts, assignProductType, prodFilterSelections]);

  const categoryOptions      = [...new Set(filteredProducts.map((x) => x.productCategory))].filter(Boolean);
  const cableTypeOptions     = [...new Set(filteredProducts.map((x) => x.cableType))].filter(Boolean);
  const codeOptions          = [...new Set(filteredProducts.map((x) => x.code))].filter(Boolean);
  const colourOptions        = [...new Set(filteredProducts.map((x) => x.color))].filter(Boolean);
  const coreOptions          = [...new Set(filteredProducts.map((x) => x.noOfCores))].filter(Boolean);
  const crossSectionOptions  = [...new Set(filteredProducts.map((x) => x.nominalCrossSection))].filter(Boolean);
  const standardOptions      = [...new Set(filteredProducts.map((x) => x.referenceOfStandard))].filter(Boolean);
  const voltageOptions       = [...new Set(filteredProducts.map((x) => x.voltage))].filter(Boolean);
  const constructionOptions  = [...new Set(filteredProducts.map((x) => x.construction))].filter(Boolean);
  const rawCategoryOptions        = [...new Set(filteredProducts.map((x) => x.productCategory))].filter(Boolean);
  const rawSubCategoryOptions     = [...new Set(filteredProducts.map((x) => x.subCategory))].filter(Boolean);
  const rawQualityCategoryOptions = [...new Set(filteredProducts.map((x) => x.qualityCategory))].filter(Boolean);

  /* ── Product tab: same filterFields shape as GTPConfiguration.js's Product Format
     tab — each entry's `key` matches the row field name directly. ── */
  const productTabFilterFields = [
    { key: "category",            label: "Category"      },
    { key: "cableType",           label: "Cable Type"    },
    { key: "code",                label: "Code"          },
    { key: "colour",              label: "Color"         },
    { key: "noOfCores",           label: "Cores"         },
    { key: "nominalCrossSection", label: "Cross Section" },
    { key: "referenceOfStandard", label: "Std. Ref"      },
    { key: "voltage",             label: "Voltage"       },
    { key: "construction",        label: "Construction"  },
  ];

  /* Row passes every currently-selected multi-select filter + the two text filters */
  const rowMatchesProductTabFilters = (row, excludeKey) => {
    for (const { key } of productTabFilterFields) {
      if (key === excludeKey) continue;
      const sel = productTabFilters[key];
      if (!sel || sel.length === 0) continue;
      const rowVal = String(row[key] || "");
      if (!sel.some((o) => o.value === rowVal)) return false;
    }
    if (productCodeSearch && !String(row.productCode || "").toLowerCase().includes(productCodeSearch.toLowerCase())) return false;
    if (productDescSearch && !String(row.product_description || "").toLowerCase().includes(productDescSearch.toLowerCase())) return false;
    return true;
  };

  const productTabFiltered = useMemo(
    () => assignedProductsList.filter((row) => rowMatchesProductTabFilters(row, null)),
    [assignedProductsList, productTabFilters, productCodeSearch, productDescSearch]
  );

  /* Each dropdown's options are faceted against every *other* selected filter, so
     choices always stay valid/cascading without needing a dedicated server call. */
  const productTabFacetOptions = (key) => {
    const rows = assignedProductsList.filter((row) => rowMatchesProductTabFilters(row, key));
    return [...new Set(rows.map((r) => String(r[key] || "")))].filter(Boolean).sort();
  };

  const productTabActiveFilterCount = Object.values(productTabFilters).reduce((s, a) => s + (a?.length || 0), 0)
    + (productCodeSearch ? 1 : 0) + (productDescSearch ? 1 : 0);

  const clearProductTabFilters = () => {
    setProductTabFilters(emptyProductTabFilters);
    setProductCodeSearch("");
    setProductDescSearch("");
  };

  /* ══════════════════════════════════════════════
     ASSIGNMENT — handlers
  ══════════════════════════════════════════════ */
  const handleOpenAssignModal = () => {
    if (!assignProductType) { alert("Please select a Product Type to assign a Checklist"); return; }
    setAssignChecklistSelected(null);
    setAssignMatchCount(prodMatchCount);
    setShowAssignModal(true);
  };

  const handleAssignSave = async () => {
    if (!assignChecklistSelected) { alert("Select a Checklist"); return; }
    setAssignSaving(true);
    try {
      const params = buildAssignParams(prodFilterSelections, assignProductType, assignProductCode);
      const r = await axios.post(`${API_BASE}/assign`, {
        checklistNumber:     assignChecklistSelected.value,
        productType:         assignProductType          || null,
        productCategory:     params.productCategory     || null,
        subCategory:         params.subCategory         || null,
        qualityCategory:     params.qualityCategory     || null,
        cableType:           params.cableType           || null,
        construction:        params.construction        || null,
        code:                params.code                || null,
        colour:              params.colour              || null,
        noOfCores:           params.noOfCores           || null,
        nominalCrossSection: params.nominalCrossSection || null,
        referenceOfStandard: params.referenceOfStandard || null,
        voltage:             params.voltage             || null,
        productCode:         params.productCode         || null,
      });
      toast.success(`Assigned — ${r.data.productsLinked} product(s)`);
      setShowAssignModal(false);
      setAssignChecklistSelected(null);
      loadAssignments();
      loadChecklistList();
      loadAssignedProducts();
    } catch (e) {
      alert(e.response?.data?.error || "Save failed");
    } finally {
      setAssignSaving(false);
    }
  };

  const handleOpenViewAssign = (row) => {
    setViewAssignRow(row);
    setShowViewAssignModal(true);
  };

  /* Product tab "View" — reuses the same View Assignment modal, looked up by
     the assignment_id carried on each product row. */
  const handleViewFromProductTab = (row) => {
    const match = assignmentList.find((a) => a.assignment_id === row.assignment_id);
    if (match) { handleOpenViewAssign(match); return; }
    // Fallback: assignment row not loaded yet — build a minimal one from the product row.
    handleOpenViewAssign({
      assignment_id: row.assignment_id,
      checklist_number: row.checklist_number,
      product_category: row.category,
      cable_type: row.cableType,
      code: row.code,
      colour: row.colour,
      no_of_cores: row.noOfCores,
      nominal_cross_section: row.nominalCrossSection,
      reference_of_standard: row.referenceOfStandard,
      voltage: row.voltage,
      construction: row.construction,
      products_linked: 1,
    });
  };

  /* Product tab "Delete" — unlinks just this one product, keeps the assignment rule */
  const handleDeleteAssignedProduct = async (row) => {
    if (!window.confirm(`Remove product "${row.productCode}" from Checklist ${row.checklist_number}?`)) return;
    try {
      await axios.delete(`${API_BASE}/assigned-products/${row.link_id}`);
      toast.success("Product removed");
      loadAssignedProducts();
      loadAssignments();
      loadChecklistList();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm("Remove this product assignment?")) return;
    try {
      await axios.delete(`${API_BASE}/assignments/${assignmentId}`);
      toast.success("Assignment removed");
      loadAssignments();
      loadChecklistList();
      loadAssignedProducts();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  /* ── Assign tab filter bar (identical layout/behaviour to InspectionCharacteristics.js) ── */
  const renderAssignFilterBar = () => {
    const isFinished = assignProductType.toLowerCase().includes("finished");
    const isRaw      = assignProductType.toLowerCase().includes("raw");

    const makeSelect = (key, label, opts) => (
      <div key={key} style={{ minWidth: 110, flex: "1 1 100px", maxWidth: 190, display: "flex", flexDirection: "column", gap: 3 }}>
        <span style={styles.filterLabel}>{label}</span>
        <Select
          isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
          options={opts}
          value={prodFilterSelections[key] || []}
          onChange={(selectedOpts) => {
            setProdFilterSelections((prev) => {
              const next = { ...prev, [key]: selectedOpts || [] };
              if (key === "productCategory") { next.cableType = []; next.code = []; next.subCategory = []; next.qualityCategory = []; }
              if (key === "cableType") { next.code = []; }
              return next;
            });
          }}
          placeholder={label} isClearable
          menuPortalTarget={document.body} menuPosition="fixed"
          styles={filterSelectStyles}
        />
      </div>
    );

    const clearAll = () => {
      setAssignProductType("");
      setProdFilterSelections(emptyProdFilters);
      setAssignProductCode("");
      setAssignProductDesc("");
      setProdMatchCount(null);
    };

    return (
      <div style={styles.filterCard}>
        {/* ROW 1 — Product Type */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12, marginBottom: 12 }}>
          <div style={{ minWidth: 200, display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={styles.filterLabel}>Product Type</span>
            <select className="form-select form-select-sm" style={{ fontSize: 12, height: 30 }}
              value={assignProductType}
              onChange={(e) => {
                setAssignProductType(e.target.value);
                setProdFilterSelections(emptyProdFilters);
                setAssignProductCode("");
                setAssignProductDesc("");
              }}>
              <option value="">Select Product Type</option>
              {(ddl.productTypes || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {prodActiveFilterCount > 0 && (
            <button onClick={clearAll} style={styles.clearBtn}>✕ Clear All ({prodActiveFilterCount})</button>
          )}
        </div>

        {/* ROW 2 — Finished Goods filters */}
        {isFinished && (
          <div style={{ ...styles.filterBar, marginBottom: 12 }}>
            {makeSelect("productCategory", "Category",      toOpts(categoryOptions))}
            {makeSelect("cableType",       "Cable Type",    toOpts(cableTypeOptions))}
            {makeSelect("code",            "Code",          toOpts(codeOptions))}
            {makeSelect("colour",          "Color",         toOpts(colourOptions))}
            {makeSelect("noOfCores",       "Cores",         toOpts(coreOptions))}
            {makeSelect("nominalCrossSection", "Cross Section", toOpts(crossSectionOptions))}
            {makeSelect("referenceOfStandard", "Std. Ref",  toOpts(standardOptions))}
            {makeSelect("voltage",         "Voltage",       toOpts(voltageOptions))}
            {makeSelect("construction",    "Construction",  toOpts(constructionOptions))}
          </div>
        )}

        {/* ROW 2 — Raw Material filters */}
        {isRaw && (
          <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
            {makeSelect("productCategory", "Category",        toOpts(rawCategoryOptions))}
            {makeSelect("subCategory",     "Sub Category",    toOpts(rawSubCategoryOptions))}
            {makeSelect("qualityCategory", "Quality Category",toOpts(rawQualityCategoryOptions))}
          </div>
        )}

        {/* ROW 3 — Product Code ↔ Description */}
        {(isFinished || isRaw) && (
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <span style={styles.filterLabel}>Product Code</span>
              <input type="text" className="form-control form-control-sm" placeholder="Product Code..."
                style={{ width: 200, height: 30, fontSize: 12 }} value={assignProductCode}
                onChange={(e) => {
                  const val = e.target.value;
                  setAssignProductCode(val);
                  if (val) {
                    const match = allProducts.find((p) => (p.productCode || "").toLowerCase() === val.toLowerCase());
                    if (match) setAssignProductDesc(match.productDescription || "");
                  } else { setAssignProductDesc(""); }
                }}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 240 }}>
              <span style={styles.filterLabel}>Product Description</span>
              <input type="text" className="form-control form-control-sm" placeholder="Product Description..."
                style={{ height: 30, fontSize: 12 }} value={assignProductDesc}
                onChange={(e) => {
                  const val = e.target.value;
                  setAssignProductDesc(val);
                  if (val) {
                    const match = allProducts.find((p) => (p.productDescription || "").toLowerCase() === val.toLowerCase());
                    if (match) setAssignProductCode(match.productCode || "");
                  } else { setAssignProductCode(""); }
                }}
              />
            </div>
            {(assignProductCode || assignProductDesc) && (
              <button style={styles.clearBtn} onClick={() => { setAssignProductCode(""); setAssignProductDesc(""); }}>× Clear</button>
            )}
          </div>
        )}

        {prodMatchCount !== null && (
          <div style={styles.matchHint}>
            <span style={{ color: prodMatchCount > 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
              {prodMatchCount > 0 ? `${prodMatchCount} product${prodMatchCount !== 1 ? "s" : ""} match` : "No products match the current filters"}
            </span>
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
          <button onClick={handleOpenAssignModal} style={{ background: "#021843", color: "#fff", border: "none", borderRadius: "4px", padding: "5px 16px", fontSize: "13px", fontWeight: 500, height: "32px", cursor: "pointer" }}>
            Assign
          </button>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <div style={{ padding: 20 }}>
        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#1e293b" }}>Checklist Configuration</div>
            <div style={{ fontSize: 13, color: "#64748b" }}>Define process parameters and assign them to products</div>
          </div>
          {tableView === "checklist" && (
            <button
              style={{ padding: "8px 16px", background: "#1e293b", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}
              onClick={openCreate}
            >
              + Create Checklist
            </button>
          )}
        </div>

        {/* TAB TOGGLE — identical pattern to InspectionCharacteristics.js */}
        <div style={{ ...styles.toggleBar, justifyContent: "flex-end" }}>
          <button style={{ ...styles.toggleBtn, ...(tableView === "checklist"   ? styles.toggleBtnActive : styles.toggleBtnInactive) }} onClick={() => setTableView("checklist")}>CHECKLIST</button>
          <button style={{ ...styles.toggleBtn, ...(tableView === "assignment" ? styles.toggleBtnActive : styles.toggleBtnInactive) }} onClick={() => setTableView("assignment")}>ASSIGNMENT</button>
          <button style={{ ...styles.toggleBtn, ...(tableView === "product" ? styles.toggleBtnActive : styles.toggleBtnInactive) }} onClick={() => setTableView("product")}>PRODUCT</button>
        </div>

        {/* ═══════════ CHECKLIST TAB ═══════════ */}
        {tableView === "checklist" && (
          <div style={{ width: "100%", background: "white", borderRadius: 6, overflowX: "auto", fontSize: "13px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f1f1f1" }}>
                  <th style={thStyle}>Checklist Number</th>
                  <th style={thStyle}>Total Parameters</th>

                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {checklistList.length === 0 ? (
                  <tr><td colSpan="4" style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>No checklists found</td></tr>
                ) : (
                  checklistList.map((item) => {
                    const paramCount = (item.parameters || []).length;
                    return (
                      <tr key={item.id} onMouseOver={(e) => (e.currentTarget.style.background = "#f8fafc")} onMouseOut={(e) => (e.currentTarget.style.background = "white")}>
                        <td style={{ ...tdStyle, fontWeight: 600, color: "#0f172a" }}>{item.checklist_number}</td>
                        <td style={tdStyle}>
                          <span style={{ background: paramCount > 0 ? "#e0f2fe" : "#f1f5f9", color: paramCount > 0 ? "#0284c7" : "#94a3b8", padding: "4px 8px", borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                            {paramCount} Defined
                          </span>
                        </td>
                      
                        <td style={tdStyle}>
                          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                            <button
                              className="btn btn-outline-dark btn-sm"
                              style={{ fontSize: "11px", padding: "2px 8px" }}
                              onClick={() => viewDetails(item)}
                            >
                              View
                            </button>
                            <button
                              title="Delete"
                              style={{
                                background: "#fff",
                                border: "1px solid #f5c2c7",
                                borderRadius: "4px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "4px 8px",
                                cursor: "pointer",
                              }}
                              onClick={() => handleDelete(item)}
                            >
                              <svg width="14" height="14" viewBox="0 0 16 16" fill="#dc3545" xmlns="http://www.w3.org/2000/svg">
                                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z" />
                                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3V2h11v1h-11Z" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* ═══════════ ASSIGNMENT TAB ═══════════ */}
        {tableView === "assignment" && (
          <>
            {renderAssignFilterBar()}
            <div style={{ overflowX: "auto" }}>
              <table className="table" style={styles.erpTable}>
                <thead>
                  <tr>
                    <th style={styles.erpTh}>Checklist Number</th>
                    <th style={styles.erpTh}>Product Type</th>
                    <th style={{ ...styles.erpTh, minWidth: 320 }}>Assigned Filters</th>
                    <th style={styles.erpTh}>Products Linked</th>
                    <th style={{ ...styles.erpTh, textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {assignmentList.length > 0 ? assignmentList.map((row) => (
                    <tr key={row.assignment_id}>
                      <td style={styles.erpTd}>{row.checklist_number}</td>
                      <td style={styles.erpTd}>{row.product_type || "—"}</td>
                      <td style={styles.erpTd}>
                        <AssignmentFilterChips row={row} />
                      </td>
                      <td style={styles.erpTd}>
                        <span style={{ background: "#e0f2fe", color: "#0369a1", borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>
                          {row.products_linked} product{row.products_linked !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td style={{ ...styles.erpTd, textAlign: "right", whiteSpace: "nowrap" }}>
                        <button className="btn btn-outline-dark btn-sm me-2" onClick={() => handleOpenViewAssign(row)}>View</button>
                        <button style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, color: "#dc2626", cursor: "pointer", padding: "4px 7px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                          onClick={() => handleDeleteAssignment(row.assignment_id)} title="Delete"><DeleteIcon /></button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="text-center text-muted p-3">No assignments found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {/* ═══════════ PRODUCT TAB ═══════════
            Same filter-bar + table pattern as GTPConfiguration.js's Product
            Format tab: isMulti cascading dropdowns (Category, Cable Type, Code,
            Color, Cores, Cross Section, Std. Ref, Voltage, Construction),
            Product Code / Description search with auto-fill, pagination, and a
            per-row View / Delete action. */}
        {tableView === "product" && (
          <>
            <div style={styles.filterCard}>
              {/* Row 1: cascading dropdowns */}
              <div style={styles.filterBar}>
                {productTabFilterFields.map(({ key, label }) => (
                  <div key={key} style={{ minWidth: 110, flex: "1 1 100px", maxWidth: 180, display: "flex", flexDirection: "column", gap: 3 }}>
                    <span style={styles.filterLabel}>{label}</span>
                    <Select
                      isMulti closeMenuOnSelect={false} hideSelectedOptions={false}
                      options={toOpts(productTabFacetOptions(key))}
                      value={productTabFilters[key]}
                      onChange={(opts) => setProductTabFilters((prev) => ({ ...prev, [key]: opts || [] }))}
                      placeholder={label} isClearable
                      menuPortalTarget={document.body} menuPosition="fixed"
                      styles={filterSelectStyles}
                    />
                  </div>
                ))}
                {productTabActiveFilterCount > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 3, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, color: "transparent" }}>x</span>
                    <button onClick={clearProductTabFilters} style={styles.clearBtn}>✕ Clear ({productTabActiveFilterCount})</button>
                  </div>
                )}
              </div>

              {productTabActiveFilterCount > 0 && (
                <div style={styles.matchHint}>
                  <span style={{ color: productTabFiltered.length > 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                    {productTabFiltered.length > 0 ? `${productTabFiltered.length} product${productTabFiltered.length !== 1 ? "s" : ""} match` : "No products match"}
                  </span>
                </div>
              )}

              {/* Row 2: Product Code + Description with auto-fill */}
              <div style={{ display: "flex", gap: 12, marginTop: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <span style={styles.filterLabel}>Product Code</span>
                  <input
                    type="text" className="form-control form-control-sm" placeholder="Search product code..."
                    style={{ width: 200, height: 30, fontSize: 12 }}
                    value={productCodeSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setProductCodeSearch(val);
                      if (val) {
                        const match = assignedProductsList.find((p) => (p.productCode || "").toLowerCase() === val.toLowerCase());
                        if (match) setProductDescSearch(match.product_description || "");
                      } else { setProductDescSearch(""); }
                    }}
                  />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 240 }}>
                  <span style={styles.filterLabel}>Product Description</span>
                  <input
                    type="text" className="form-control form-control-sm" placeholder="Search product description..."
                    style={{ height: 30, fontSize: 12 }}
                    value={productDescSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setProductDescSearch(val);
                      if (val) {
                        const match = assignedProductsList.find((p) => (p.product_description || "").toLowerCase() === val.toLowerCase());
                        if (match) setProductCodeSearch(match.productCode || "");
                      } else { setProductCodeSearch(""); }
                    }}
                  />
                </div>
                {(productCodeSearch || productDescSearch) && (
                  <button style={styles.clearBtn} onClick={() => { setProductCodeSearch(""); setProductDescSearch(""); }}>× Clear</button>
                )}
              </div>
            </div>

            {/* Table — scrollable, compact, paginated */}
            {(() => {
              const tdSm = { ...styles.erpTd, fontSize: 11, padding: "7px 8px" };
              const totalItems = productTabFiltered.length;
              const totalPages = Math.max(1, Math.ceil(totalItems / PRODUCT_TAB_PAGE_SIZE));
              const safePage = Math.min(productTabPage, totalPages);
              const startIdx = (safePage - 1) * PRODUCT_TAB_PAGE_SIZE;
              const pageItems = productTabFiltered.slice(startIdx, startIdx + PRODUCT_TAB_PAGE_SIZE);

              return (
                <>
                  <div style={{ overflowX: "auto" }}>
                    <table className="table" style={{ ...styles.erpTable, fontSize: 12, whiteSpace: "nowrap" }}>
                      <thead>
                        <tr>
                          <th style={styles.erpTh}>Checklist No.</th>
                          <th style={styles.erpTh}>Product Code</th>
                          <th style={styles.erpTh}>Product Description</th>
                          <th style={styles.erpTh}>Category</th>
                          <th style={styles.erpTh}>Cable Type</th>
                          <th style={styles.erpTh}>Code</th>
                          <th style={styles.erpTh}>Color</th>
                          <th style={styles.erpTh}>Cores</th>
                          <th style={styles.erpTh}>Cross Section</th>
                          <th style={styles.erpTh}>Std. Ref</th>
                          <th style={styles.erpTh}>Voltage</th>
                          <th style={styles.erpTh}>Construction</th>
                          <th style={styles.erpTh}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageItems.length > 0 ? pageItems.map((row) => (
                          <tr key={row.link_id}>
                            <td style={tdSm}>{row.checklist_number}</td>
                            <td style={tdSm}>{row.productCode || "—"}</td>
                            <td style={{ ...tdSm, whiteSpace: "normal", minWidth: 200 }}>{row.product_description || "—"}</td>
                            <td style={tdSm}>{row.category || "—"}</td>
                            <td style={tdSm}>{row.cableType || "—"}</td>
                            <td style={tdSm}>{row.code || "—"}</td>
                            <td style={tdSm}>{row.colour || "—"}</td>
                            <td style={tdSm}>{row.noOfCores || "—"}</td>
                            <td style={tdSm}>{row.nominalCrossSection || "—"}</td>
                            <td style={tdSm}>{row.referenceOfStandard || "—"}</td>
                            <td style={tdSm}>{row.voltage || "—"}</td>
                            <td style={tdSm}>{row.construction || "—"}</td>
                            <td style={{ ...tdSm, whiteSpace: "nowrap" }}>
                              <button className="btn btn-outline-dark btn-sm me-1" onClick={() => handleViewFromProductTab(row)}>View</button>
                              <button
                                style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 6, color: "#dc2626", cursor: "pointer", padding: "4px 7px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                                onClick={() => handleDeleteAssignedProduct(row)} title="Delete"
                              >
                                <DeleteIcon />
                              </button>
                            </td>
                          </tr>
                        )) : (
                          <tr><td colSpan="13" className="text-center text-muted p-3">No assigned products found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination controls */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, flexWrap: "wrap", gap: 8 }}>
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      {totalItems > 0
                        ? `Showing ${startIdx + 1}-${Math.min(startIdx + PRODUCT_TAB_PAGE_SIZE, totalItems)} of ${totalItems}`
                        : "Showing 0 of 0"}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: "auto" }}>
                      <button
                        style={{ fontSize: 13, fontWeight: 600, padding: "5px 14px", borderRadius: 4, border: "1px solid #cbd5e1", background: "#fff", color: "#334155", cursor: safePage <= 1 ? "not-allowed" : "pointer", opacity: safePage <= 1 ? 0.5 : 1 }}
                        disabled={safePage <= 1}
                        onClick={() => setProductTabPage(safePage - 1)}
                      >Prev</button>
                      <span style={{ fontSize: 13, color: "#64748b" }}>Page {safePage} of {totalPages}</span>
                      <button
                        style={{ fontSize: 13, fontWeight: 600, padding: "5px 14px", borderRadius: 4, border: "1px solid #cbd5e1", background: "#fff", color: "#334155", cursor: safePage >= totalPages ? "not-allowed" : "pointer", opacity: safePage >= totalPages ? 0.5 : 1 }}
                        disabled={safePage >= totalPages}
                        onClick={() => setProductTabPage(safePage + 1)}
                      >Next</button>
                    </div>
                  </div>
                </>
              );
            })()}
          </>
        )}

        {/* CREATE / EDIT MODAL */}
        {showModal && (
          <>
            <div onClick={() => setShowModal(false)} style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100 }} />
            <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "900px", maxWidth: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column", background: "white", zIndex: 1101, borderRadius: 8, boxShadow: "0 10px 30px rgba(0,0,0,0.2)" }}>
              <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, color: "#1e293b", fontSize: 18, fontWeight: 600 }}>
                  {editingId ? "Checklist" : "Create Checklist"}
                </h3>
                <button onClick={() => setShowModal(false)} style={{ background: "transparent", border: "none", fontSize: 22, cursor: "pointer", color: "#64748b", lineHeight: 1 }}>&times;</button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", background: "#fafafa" }}>
                {/* Frozen top: tabs + Add Parameter button (does not scroll) */}
                <div style={{ padding: "24px 24px 0 24px" }}>
                  <div style={{ background: "white", padding: "20px 20px 0 20px", borderRadius: "6px 6px 0 0", border: "1px solid #e2e8f0", borderBottom: "none" }}>
                    {/* Checklist Number — editable at creation time only; fixed afterwards */}
                    <div style={{ marginBottom: 16, maxWidth: 260 }}>
                      <label style={{ ...smallLabel, marginBottom: 4 }}>
                        Checklist Number {!editingId && <span style={{ fontWeight: 400, color: "#94a3b8" }}>(optional — auto-generated if left blank)</span>}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="e.g. 105"
                        style={compactInput}
                        value={checklistNumberInput}
                        disabled={!!editingId}
                        onChange={(e) => setChecklistNumberInput(e.target.value.replace(/[^\d]/g, ""))}
                      />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 16 }}>
                      <div style={{ display: "flex", gap: 12 }}>
                        <button onClick={() => setActiveTab("parameters")} style={{ padding: "8px 14px", border: "none", borderBottom: activeTab === "parameters" ? "3px solid #1976d2" : "3px solid transparent", background: "transparent", fontWeight: activeTab === "parameters" ? 700 : 500, cursor: "pointer" }}>Process Parameters</button>
                        <button onClick={() => setActiveTab("changeLog")} style={{ padding: "8px 14px", border: "none", borderBottom: activeTab === "changeLog" ? "3px solid #1976d2" : "3px solid transparent", background: "transparent", fontWeight: activeTab === "changeLog" ? 700 : 500, cursor: "pointer" }}>Change Log</button>
                      </div>
                      {activeTab === "parameters" && (
                        <button onClick={addParameter} disabled={parameters.length >= 30} style={{ padding: "6px 14px", background: parameters.length >= 30 ? "#cbd5e1" : "#1e293b", color: "white", border: "none", borderRadius: 4, cursor: parameters.length >= 30 ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600 }}>+ Add Parameter</button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Scrollable body: only the table rows scroll, header row(s) stay visible */}
                <div style={{ flex: 1, overflowY: "auto", padding: "0 24px 24px 24px" }}>
                  <div style={{ background: "white", padding: "0 20px 20px 20px", borderRadius: "0 0 6px 6px", border: "1px solid #e2e8f0", borderTop: "none" }}>
                    {activeTab === "parameters" ? (
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                            <th style={{ position: "sticky", top: 0, background: "white", zIndex: 2, padding: "8px 12px", textAlign: "left", width: "50px", color: "#64748b" }}>Item</th>
                            <th style={{ position: "sticky", top: 0, background: "white", zIndex: 2, padding: "8px 12px", textAlign: "left", color: "#64748b" }}>Parameter Name</th>
                            <th style={{ position: "sticky", top: 0, background: "white", zIndex: 2, padding: "8px 12px", textAlign: "left", width: "150px", color: "#64748b" }}>UOM</th>
                            <th style={{ position: "sticky", top: 0, background: "white", zIndex: 2, padding: "8px 12px", textAlign: "left", width: "260px", color: "#64748b" }}>Type of Analysis</th>
                            <th style={{ position: "sticky", top: 0, background: "white", zIndex: 2, padding: "8px 12px", textAlign: "center", width: "60px", color: "#64748b" }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {parameters.map((param, index) => (
                            <tr key={index} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "10px 12px", fontWeight: 600, color: "#94a3b8" }}>{index + 1}.</td>
                              <td style={{ padding: "10px 12px" }}>
                                <input
                                  type="text"
                                  className="form-control"
                                  placeholder="Enter parameter name"
                                  style={compactInput}
                                  value={param.name}
                                  onChange={(e) => updateParameter(index, "name", e.target.value)}
                                />
                              </td>
                              <td style={{ padding: "10px 12px" }}>
                                <Select
                                  options={uomOptions}
                                  value={uomOptions.find((opt) => opt.value === param.uom) || null}
                                  onChange={(selected) => updateParameter(index, "uom", selected ? selected.value : "")}
                                  placeholder="Select UOM"
                                  isClearable
                                  menuPortalTarget={document.body}
                                  menuPosition={"fixed"}
                                  styles={customSelectStyles}
                                  components={{ SingleValue: SingleValueWithTooltip }}
                                />
                              </td>
                              <td style={{ padding: "10px 12px" }}>
                                <Select
                                  options={analysisOptions}
                                  value={analysisOptions.find((opt) => opt.value === param.analysisType) || null}
                                  onChange={(selected) => updateParameter(index, "analysisType", selected ? selected.value : "")}
                                  placeholder="Select Type"
                                  isClearable
                                  menuPortalTarget={document.body}
                                  menuPosition={"fixed"}
                                  styles={customSelectStyles}
                                  components={{ SingleValue: SingleValueWithTooltip }}
                                />
                              </td>
                              <td style={{ padding: "10px 12px", textAlign: "center" }}>
                                <button
                                  onClick={() => removeParameter(index)}
                                  title="Delete Parameter"
                                  style={{
                                    background: "#fff",
                                    border: "1px solid #f5c2c7",
                                    borderRadius: "4px",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    padding: "4px 8px",
                                    cursor: "pointer",
                                  }}
                                >
                                  <FaTrashAlt size={12} color="#dc3545" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div style={{ background: "#fff", borderRadius: 6 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                          <thead>
                            <tr style={{ borderBottom: "2px solid #e2e8f0" }}>
                              <th style={{ ...thStyle, position: "sticky", top: 0, zIndex: 2 }}>Date & Time</th>
                              <th style={{ ...thStyle, position: "sticky", top: 0, zIndex: 2 }}>User</th>
                              <th style={{ ...thStyle, position: "sticky", top: 0, zIndex: 2 }}>Action</th>
                              <th style={{ ...thStyle, position: "sticky", top: 0, zIndex: 2 }}>Field</th>
                              <th style={{ ...thStyle, position: "sticky", top: 0, zIndex: 2 }}>Old Value</th>
                              <th style={{ ...thStyle, position: "sticky", top: 0, zIndex: 2 }}>New Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {checklistLogs.length > 0 ? (
                              checklistLogs.map((log) => (
                                <tr key={log.id}>
                                  <td style={tdStyle}>{log.created_at ? new Date(log.created_at).toLocaleString() : "-"}</td>
                                  <td style={tdStyle}>{log.created_by || "-"}</td>
                                  <td style={tdStyle}>{log.action_type || "-"}</td>
                                  <td style={tdStyle}>{log.field_name || "-"}</td>
                                  <td style={tdStyle}>{log.old_value || "-"}</td>
                                  <td style={{ ...tdStyle, fontWeight: 700 }}>{log.new_value || "-"}</td>
                                </tr>
                              ))
                            ) : (
                              <tr><td colSpan="6" style={{ textAlign: "center", padding: 20 }}>No change logs available</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {errorMsg && (
                    <div style={{ marginTop: 16, padding: "10px 12px", background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 6, fontSize: 13 }}>
                      {errorMsg}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ padding: "16px 24px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 12, background: "white", borderRadius: "0 0 8px 8px" }}>
                <button onClick={() => setShowModal(false)} disabled={saving} style={{ padding: "8px 16px", background: "#f1f5f9", border: "1px solid #cbd5e1", color: "#475569", borderRadius: 4, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", background: "#28a745", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                  {saving ? "Saving…" : editingId ? "Update Configuration" : "Save Configuration"}
                </button>
              </div>
            </div>
          </>
        )}

        {/* ═══════════════════════════════════════════════
            MODAL — ASSIGN CHECKLIST TO PRODUCTS
            (mirrors InspectionCharacteristics.js's Assign modal)
        ═══════════════════════════════════════════════ */}
        {showAssignModal && (
          <div style={styles.modalBackdrop}>
            <div style={{ ...styles.modal, maxWidth: 860, height: "auto", maxHeight: "90vh" }}>
              <div style={styles.modalHeader}>
                <h5 style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>Assign Checklist</h5>
                <button style={styles.closeBtn} onClick={() => { setShowAssignModal(false); setAssignChecklistSelected(null); }}>✖</button>
              </div>
              <div style={{ ...styles.modalBody, padding: "24px 28px", overflowY: "auto" }}>
                {/* Applied filters summary */}
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 20 }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {assignProductType && (
                      <div style={{ background: "#e2e8f0", borderRadius: 4, padding: "3px 10px", fontSize: 12 }}>
                        <span style={{ color: "#64748b", fontWeight: 600 }}>Product Type: </span>
                        <span style={{ color: "#1e293b" }}>{assignProductType}</span>
                      </div>
                    )}
                    {[
                      ["Category",       prodFilterSelections.productCategory],
                      ["Sub Category",   prodFilterSelections.subCategory],
                      ["Quality Cat.",   prodFilterSelections.qualityCategory],
                      ["Cable Type",     prodFilterSelections.cableType],
                      ["Code",           prodFilterSelections.code],
                      ["Color",          prodFilterSelections.colour],
                      ["Cores",          prodFilterSelections.noOfCores],
                      ["Cross Section",  prodFilterSelections.nominalCrossSection],
                      ["Std. Ref",       prodFilterSelections.referenceOfStandard],
                      ["Voltage",        prodFilterSelections.voltage],
                      ["Construction",   prodFilterSelections.construction],
                    ].map(([label, arr]) => {
                      const val = arr?.map((o) => o.value).join(", ");
                      return val ? (
                        <div key={label} style={{ background: "#e2e8f0", borderRadius: 4, padding: "3px 10px", fontSize: 12 }}>
                          <span style={{ color: "#64748b", fontWeight: 600 }}>{label}: </span>
                          <span style={{ color: "#1e293b" }}>{val}</span>
                        </div>
                      ) : null;
                    })}
                    {assignProductCode && (
                      <div style={{ background: "#e2e8f0", borderRadius: 4, padding: "3px 10px", fontSize: 12 }}>
                        <span style={{ color: "#64748b", fontWeight: 600 }}>Product Code: </span>
                        <span style={{ color: "#1e293b" }}>{assignProductCode}</span>
                      </div>
                    )}
                  </div>
                  {assignMatchCount !== null && (
                    <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: assignMatchCount > 0 ? "#16a34a" : "#dc2626" }}>
                      {assignMatchCount > 0 ? `${assignMatchCount} product${assignMatchCount !== 1 ? "s" : ""} will be assigned` : "No products match the current filters"}
                    </div>
                  )}
                </div>

                {/* Checklist selector */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ ...styles.compactLabel, fontSize: 13 }}>Select Checklist <span style={{ color: "#dc2626" }}>*</span></label>
                  <Select
                    options={checklistOptions}
                    value={assignChecklistSelected}
                    onChange={setAssignChecklistSelected}
                    placeholder="Search and select a Checklist..."
                    isClearable styles={customSelectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="auto"
                  />
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>Select a checklist to assign to the matched products.</div>
                </div>

                {/* Parameters preview */}
                {assignChecklistSelected && (
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>
                      Parameters <span style={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(will apply to all matched products)</span>
                    </div>
                    <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "#f8fafc" }}>
                            <th style={{ ...styles.erpTh, width: "50%" }}>Parameter</th>
                            <th style={{ ...styles.erpTh, width: "20%" }}>UOM</th>
                            <th style={{ ...styles.erpTh, width: "30%" }}>Type of Analysis</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(checklistList.find((c) => c.checklist_number === assignChecklistSelected.value)?.parameters || []).map((p, i) => (
                            <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ ...styles.erpTd, fontSize: 13 }}>{p.name}</td>
                              <td style={{ ...styles.erpTd, fontSize: 12, color: "#64748b" }}>{p.uom || "—"}</td>
                              <td style={{ ...styles.erpTd, fontSize: 12 }}>{p.analysisType || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              <div style={{ ...styles.modalFooter, gap: 10 }}>
                <button style={{ ...styles.saveBtn, background: "#64748b" }} onClick={() => { setShowAssignModal(false); setAssignChecklistSelected(null); }}>Cancel</button>
                <button
                  style={{ ...styles.saveBtn, opacity: (!assignChecklistSelected || assignSaving) ? 0.6 : 1, cursor: (!assignChecklistSelected || assignSaving) ? "not-allowed" : "pointer" }}
                  disabled={!assignChecklistSelected || assignSaving}
                  onClick={handleAssignSave}
                >
                  {assignSaving ? "Saving..." : "Assign"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════════════════════════════════════
            MODAL — VIEW ASSIGNMENT
        ═══════════════════════════════════════════════ */}
        {showViewAssignModal && viewAssignRow && (
          <div style={styles.modalBackdrop}>
            <div style={{ ...styles.modal, maxWidth: 860, height: "auto", maxHeight: "92vh" }}>
              <div style={styles.modalHeader}>
                <h5 style={{ margin: 0, fontWeight: 700, color: "#1e293b" }}>
                  Checklist <span style={{ color: "#021843" }}>{viewAssignRow.checklist_number}</span>
                </h5>
                <button style={styles.closeBtn} onClick={() => { setShowViewAssignModal(false); setViewAssignRow(null); }}>✖</button>
              </div>

              <div style={{ ...styles.modalBody, padding: "24px 28px", overflowY: "auto" }}>
                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px", marginBottom: 20 }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {[
                      ["Product Type",   viewAssignRow.product_type],
                      ["Category",       viewAssignRow.product_category],
                      ["Sub Category",   viewAssignRow.sub_category],
                      ["Quality Cat.",   viewAssignRow.quality_category],
                      ["Cable Type",     viewAssignRow.cable_type],
                      ["Code",           viewAssignRow.code],
                      ["Color",          viewAssignRow.colour],
                      ["Cores",          viewAssignRow.no_of_cores],
                      ["Cross Section",  viewAssignRow.nominal_cross_section],
                      ["Std. Ref",       viewAssignRow.reference_of_standard],
                      ["Voltage",        viewAssignRow.voltage],
                      ["Construction",   viewAssignRow.construction],
                      ["Product Code",   viewAssignRow.product_code_filter],
                    ].filter(([, v]) => v).map(([label, value]) => (
                      <div key={label} style={{ background: "#e2e8f0", borderRadius: 4, padding: "3px 10px", fontSize: 12 }}>
                        <span style={{ color: "#64748b", fontWeight: 600 }}>{label}: </span>
                        <span style={{ color: "#1e293b" }}>{value}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600, color: "#16a34a" }}>
                    {viewAssignRow.products_linked} product{viewAssignRow.products_linked !== 1 ? "s" : ""} linked
                  </div>
                </div>

                <div style={{ fontSize: 13, fontWeight: 700, color: "#334155", marginBottom: 8 }}>Parameters</div>
                <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f8fafc" }}>
                        <th style={{ ...styles.erpTh, width: "50%" }}>Parameter</th>
                        <th style={{ ...styles.erpTh, width: "20%" }}>UOM</th>
                        <th style={{ ...styles.erpTh, width: "30%" }}>Type of Analysis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(checklistList.find((c) => c.checklist_number === viewAssignRow.checklist_number)?.parameters || []).map((p, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ ...styles.erpTd, fontSize: 13 }}>{p.name}</td>
                          <td style={{ ...styles.erpTd, fontSize: 12, color: "#64748b" }}>{p.uom || "—"}</td>
                          <td style={{ ...styles.erpTd, fontSize: 12 }}>{p.analysisType || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ ...styles.modalFooter, gap: 10 }}>
                <button style={{ ...styles.saveBtn, background: "#64748b" }} onClick={() => { setShowViewAssignModal(false); setViewAssignRow(null); }}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Checklist;